// Side game money calculation engine
// Pure functions — no side effects

import { stablefordPoints } from './scoringEngine'

/**
 * Compute CTP money for a single round.
 * Each par 3 with a winner: winner gets (numPlayers × amount), everyone else loses amount.
 * @param {Array} ctpResults - [{ hole_number, winner_player_id }]
 * @param {Object} sideBet - { amount }
 * @param {Array} playerIds - all player IDs in the round
 * @param {Array} par3Holes - hole numbers that are par 3
 * @returns {Object} { byPlayer: { [playerId]: amount }, details: [{ hole, winnerId, pot }] }
 */
export function computeCtpMoney(ctpResults, sideBet, playerIds, par3Holes) {
  const amount = Number(sideBet.amount)
  const byPlayer = {}
  playerIds.forEach((pid) => { byPlayer[pid] = 0 })

  const details = []

  for (const hole of par3Holes) {
    const result = ctpResults.find((r) => r.hole_number === hole)
    if (!result) continue

    const winnerId = result.winner_player_id
    const pot = amount * playerIds.length

    // Everyone pays in
    playerIds.forEach((pid) => { byPlayer[pid] -= amount })
    // Winner gets the pot
    if (byPlayer[winnerId] !== undefined) {
      byPlayer[winnerId] += pot
    }

    details.push({ hole, winnerId, pot })
  }

  return { byPlayer, details }
}

/**
 * Compute Lowest Net money for a single round.
 * Winner gets (numPlayers × amount), everyone else loses amount.
 * Ties split the pot evenly among tied players.
 * @param {Array} scores - [{ player_id, hole_number, strokes }]
 * @param {Object} sideBet - { amount }
 * @param {Array} handicapBreakdown - [{ playerId, strokeHoles: Map }]
 * @param {number} numHoles - 9 or 18
 * @param {Array} playerIds - all player IDs in the round
 * @returns {Object} { byPlayer: { [playerId]: amount }, winner: playerId|null, tied: boolean }
 */
export function computeLowNetMoney(scores, sideBet, handicapBreakdown, numHoles, playerIds) {
  const amount = Number(sideBet.amount)
  const byPlayer = {}
  playerIds.forEach((pid) => { byPlayer[pid] = 0 })

  // Calculate net totals for each player
  const netTotals = playerIds.map((pid) => {
    let netTotal = 0
    let holesPlayed = 0
    for (let hole = 1; hole <= numHoles; hole++) {
      const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === hole)
      if (s?.strokes != null) {
        const hcp = handicapBreakdown?.find((h) => h.playerId === pid)
        const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
        netTotal += s.strokes - strokesOnHole
        holesPlayed++
      }
    }
    return { playerId: pid, netTotal, holesPlayed }
  })

  // Count players who have scored at least one hole (partial rounds still count)
  const completePlayers = netTotals.filter((p) => p.holesPlayed > 0)
  if (completePlayers.length === 0) {
    return { byPlayer, winner: null, tied: false }
  }

  const lowestNet = Math.min(...completePlayers.map((p) => p.netTotal))
  const winners = completePlayers.filter((p) => p.netTotal === lowestNet)

  const totalPot = amount * playerIds.length
  const perWinner = totalPot / winners.length

  // Everyone pays in
  playerIds.forEach((pid) => { byPlayer[pid] -= amount })
  // Winners split the pot
  winners.forEach((w) => { byPlayer[w.playerId] += perWinner })

  return {
    byPlayer,
    winner: winners.length === 1 ? winners[0].playerId : null,
    tied: winners.length > 1,
    winners: winners.map((w) => w.playerId),
    lowestNet,
  }
}

/**
 * Compute match-long Lowest Net money across multiple rounds.
 * Cumulative net total across all completed rounds.
 * @param {Array} roundsData - [{ scores, handicapBreakdown, numHoles, playerIds }]
 * @param {Object} sideBet - { amount }
 * @param {Array} allPlayerIds - all players in the match
 * @returns {Object} { byPlayer: { [playerId]: amount }, winner, tied, winners, lowestNet }
 */
/**
 * Compute Skins money for a single round.
 * Lowest net score on each hole wins a skin. Pot split proportionally by skins won.
 * @param {Array} scores - [{ player_id, hole_number, strokes }]
 * @param {Object} sideBet - { amount }
 * @param {Array} handicapBreakdown - [{ playerId, strokeHoles: Map }]
 * @param {number} numHoles - 9 or 18
 * @param {Array} playerIds - all player IDs in the round
 * @param {boolean} carryOver - whether tied holes carry skins forward
 * @returns {Object} { byPlayer, skinsByPlayer, totalSkins, details }
 */
export function computeSkinsMoney(scores, sideBet, handicapBreakdown, numHoles, playerIds, carryOver, grossTiebreaker) {
  const amount = Number(sideBet.amount)
  const byPlayer = {}
  const skinsByPlayer = {}
  playerIds.forEach((pid) => { byPlayer[pid] = 0; skinsByPlayer[pid] = 0 })

  const details = []
  let pendingSkins = 0

  for (let hole = 1; hole <= numHoles; hole++) {
    const skinsAtStake = 1 + pendingSkins

    // Compute net scores for players who have a score on this hole
    const holeScores = []
    for (const pid of playerIds) {
      const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === hole)
      if (s?.strokes != null) {
        const hcp = handicapBreakdown?.find((h) => h.playerId === pid)
        const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
        holeScores.push({ playerId: pid, gross: s.strokes, net: s.strokes - strokesOnHole })
      }
    }

    if (holeScores.length === 0) {
      if (carryOver) pendingSkins = skinsAtStake
      continue
    }

    const lowestNet = Math.min(...holeScores.map((h) => h.net))
    let winners = holeScores.filter((h) => h.net === lowestNet)

    // Low gross wins: if multiple players tied on net, lowest gross wins
    if (grossTiebreaker && winners.length > 1) {
      const lowestGross = Math.min(...winners.map((h) => h.gross))
      const grossWinners = winners.filter((h) => h.gross === lowestGross)
      if (grossWinners.length < winners.length) {
        winners = grossWinners
      }
    }

    if (winners.length === 1) {
      // Single winner takes the skins
      skinsByPlayer[winners[0].playerId] += skinsAtStake
      details.push({ hole, winnerId: winners[0].playerId, skinsWon: skinsAtStake })
      pendingSkins = 0
    } else {
      // Tied — no skin awarded
      if (carryOver) {
        pendingSkins = skinsAtStake
      } else {
        pendingSkins = 0
      }
      details.push({ hole, winnerId: null, skinsWon: 0 })
    }
  }

  const totalSkins = Object.values(skinsByPlayer).reduce((s, v) => s + v, 0)

  if (totalSkins > 0) {
    const totalPot = amount * playerIds.length
    // Everyone pays in
    playerIds.forEach((pid) => { byPlayer[pid] -= amount })
    // Distribute proportionally by skins won
    for (const pid of playerIds) {
      if (skinsByPlayer[pid] > 0) {
        byPlayer[pid] += (skinsByPlayer[pid] / totalSkins) * totalPot
      }
    }
  }

  return { byPlayer, skinsByPlayer, totalSkins, details }
}

/**
 * Compute match-long Skins money across multiple rounds.
 * Carry-over does NOT span across rounds (resets each round).
 * Accumulates skins across rounds, then splits one pot proportionally.
 * @param {Array} roundsData - [{ scores, handicapBreakdown, numHoles, playerIds }]
 * @param {Object} sideBet - { amount }
 * @param {Array} allPlayerIds - all players in the match
 * @param {boolean} carryOver - whether tied holes carry skins forward within each round
 * @returns {Object} { byPlayer, skinsByPlayer, totalSkins }
 */
export function computeMatchSkinsMoney(roundsData, sideBet, allPlayerIds, carryOver, grossTiebreaker) {
  const amount = Number(sideBet.amount)
  const byPlayer = {}
  const skinsByPlayer = {}
  allPlayerIds.forEach((pid) => { byPlayer[pid] = 0; skinsByPlayer[pid] = 0 })

  for (const round of roundsData) {
    const { scores, handicapBreakdown, numHoles, playerIds } = round
    let pendingSkins = 0

    for (let hole = 1; hole <= numHoles; hole++) {
      const skinsAtStake = 1 + pendingSkins

      const holeScores = []
      for (const pid of playerIds) {
        const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === hole)
        if (s?.strokes != null) {
          const hcp = handicapBreakdown?.find((h) => h.playerId === pid)
          const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
          holeScores.push({ playerId: pid, gross: s.strokes, net: s.strokes - strokesOnHole })
        }
      }

      if (holeScores.length === 0) {
        if (carryOver) pendingSkins = skinsAtStake
        continue
      }

      const lowestNet = Math.min(...holeScores.map((h) => h.net))
      let winners = holeScores.filter((h) => h.net === lowestNet)

      // Low gross wins: if multiple players tied on net, lowest gross wins
      if (grossTiebreaker && winners.length > 1) {
        const lowestGross = Math.min(...winners.map((h) => h.gross))
        const grossWinners = winners.filter((h) => h.gross === lowestGross)
        if (grossWinners.length < winners.length) {
          winners = grossWinners
        }
      }

      if (winners.length === 1) {
        skinsByPlayer[winners[0].playerId] += skinsAtStake
        pendingSkins = 0
      } else {
        pendingSkins = carryOver ? skinsAtStake : 0
      }
    }
  }

  const totalSkins = Object.values(skinsByPlayer).reduce((s, v) => s + v, 0)

  if (totalSkins > 0) {
    const totalPot = amount * allPlayerIds.length
    allPlayerIds.forEach((pid) => { byPlayer[pid] -= amount })
    for (const pid of allPlayerIds) {
      if (skinsByPlayer[pid] > 0) {
        byPlayer[pid] += (skinsByPlayer[pid] / totalSkins) * totalPot
      }
    }
  }

  return { byPlayer, skinsByPlayer, totalSkins }
}

/**
 * Compute match-long Lowest Gross money across multiple rounds.
 * Cumulative raw strokes (no handicap) across all completed rounds.
 * @param {Array} roundsData - [{ scores, numHoles, playerIds }]
 * @param {Object} sideBet - { amount }
 * @param {Array} allPlayerIds - all players in the match
 * @returns {Object} { byPlayer, winner, tied, winners, lowestGross }
 */
export function computeMatchLowGrossMoney(roundsData, sideBet, allPlayerIds) {
  const amount = Number(sideBet.amount)
  const byPlayer = {}
  allPlayerIds.forEach((pid) => { byPlayer[pid] = 0 })

  const cumulativeGross = {}
  const roundsPlayed = {}
  allPlayerIds.forEach((pid) => { cumulativeGross[pid] = 0; roundsPlayed[pid] = 0 })

  for (const round of roundsData) {
    const { scores, numHoles, playerIds } = round
    for (const pid of playerIds) {
      let grossTotal = 0
      let holesPlayed = 0
      for (let hole = 1; hole <= numHoles; hole++) {
        const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === hole)
        if (s?.strokes != null) {
          grossTotal += s.strokes
          holesPlayed++
        }
      }
      if (holesPlayed > 0) {
        cumulativeGross[pid] += grossTotal
        roundsPlayed[pid]++
      }
    }
  }

  const eligiblePlayers = allPlayerIds.filter((pid) => roundsPlayed[pid] > 0)
  if (eligiblePlayers.length === 0) {
    return { byPlayer, winner: null, tied: false }
  }

  const lowestGross = Math.min(...eligiblePlayers.map((pid) => cumulativeGross[pid]))
  const winners = eligiblePlayers.filter((pid) => cumulativeGross[pid] === lowestGross)

  const totalPot = amount * allPlayerIds.length
  const perWinner = totalPot / winners.length

  allPlayerIds.forEach((pid) => { byPlayer[pid] -= amount })
  winners.forEach((pid) => { byPlayer[pid] += perWinner })

  return {
    byPlayer,
    winner: winners.length === 1 ? winners[0] : null,
    tied: winners.length > 1,
    winners,
    lowestGross,
  }
}

/**
 * Compute match-long Stableford money across multiple rounds.
 * Cumulative Stableford points (with handicap) — highest total wins.
 * @param {Array} roundsData - [{ scores, handicapBreakdown, numHoles, playerIds, holePars }]
 * @param {Object} sideBet - { amount }
 * @param {Array} allPlayerIds - all players in the match
 * @returns {Object} { byPlayer, winner, tied, winners, highestPoints }
 */
export function computeMatchStablefordMoney(roundsData, sideBet, allPlayerIds) {
  const amount = Number(sideBet.amount)
  const byPlayer = {}
  allPlayerIds.forEach((pid) => { byPlayer[pid] = 0 })

  const cumulativePoints = {}
  const roundsPlayed = {}
  allPlayerIds.forEach((pid) => { cumulativePoints[pid] = 0; roundsPlayed[pid] = 0 })

  for (const round of roundsData) {
    const { scores, handicapBreakdown, numHoles, playerIds, holePars } = round
    if (!holePars) continue
    for (const pid of playerIds) {
      let totalPoints = 0
      let holesPlayed = 0
      for (let hole = 1; hole <= numHoles; hole++) {
        const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === hole)
        if (s?.strokes != null) {
          const hcp = handicapBreakdown?.find((h) => h.playerId === pid)
          const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
          const netScore = s.strokes - strokesOnHole
          const par = holePars[hole - 1]
          const pts = stablefordPoints(netScore, par)
          if (pts != null) totalPoints += pts
          holesPlayed++
        }
      }
      if (holesPlayed > 0) {
        cumulativePoints[pid] += totalPoints
        roundsPlayed[pid]++
      }
    }
  }

  const eligiblePlayers = allPlayerIds.filter((pid) => roundsPlayed[pid] > 0)
  if (eligiblePlayers.length === 0) {
    return { byPlayer, winner: null, tied: false }
  }

  const highestPoints = Math.max(...eligiblePlayers.map((pid) => cumulativePoints[pid]))
  const winners = eligiblePlayers.filter((pid) => cumulativePoints[pid] === highestPoints)

  const totalPot = amount * allPlayerIds.length
  const perWinner = totalPot / winners.length

  allPlayerIds.forEach((pid) => { byPlayer[pid] -= amount })
  winners.forEach((pid) => { byPlayer[pid] += perWinner })

  return {
    byPlayer,
    winner: winners.length === 1 ? winners[0] : null,
    tied: winners.length > 1,
    winners,
    highestPoints,
  }
}

export function computeMatchLowNetMoney(roundsData, sideBet, allPlayerIds) {
  const amount = Number(sideBet.amount)
  const byPlayer = {}
  allPlayerIds.forEach((pid) => { byPlayer[pid] = 0 })

  // Accumulate net totals across all rounds
  const cumulativeNet = {}
  const roundsPlayed = {}
  allPlayerIds.forEach((pid) => { cumulativeNet[pid] = 0; roundsPlayed[pid] = 0 })

  for (const round of roundsData) {
    const { scores, handicapBreakdown, numHoles, playerIds } = round
    for (const pid of playerIds) {
      let netTotal = 0
      let holesPlayed = 0
      for (let hole = 1; hole <= numHoles; hole++) {
        const s = scores.find((sc) => sc.player_id === pid && sc.hole_number === hole)
        if (s?.strokes != null) {
          const hcp = handicapBreakdown?.find((h) => h.playerId === pid)
          const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
          netTotal += s.strokes - strokesOnHole
          holesPlayed++
        }
      }
      if (holesPlayed > 0) {
        cumulativeNet[pid] += netTotal
        roundsPlayed[pid]++
      }
    }
  }

  // Only count players who played at least one round
  const eligiblePlayers = allPlayerIds.filter((pid) => roundsPlayed[pid] > 0)
  if (eligiblePlayers.length === 0) {
    return { byPlayer, winner: null, tied: false }
  }

  const lowestNet = Math.min(...eligiblePlayers.map((pid) => cumulativeNet[pid]))
  const winners = eligiblePlayers.filter((pid) => cumulativeNet[pid] === lowestNet)

  const totalPot = amount * allPlayerIds.length
  const perWinner = totalPot / winners.length

  allPlayerIds.forEach((pid) => { byPlayer[pid] -= amount })
  winners.forEach((pid) => { byPlayer[pid] += perWinner })

  return {
    byPlayer,
    winner: winners.length === 1 ? winners[0] : null,
    tied: winners.length > 1,
    winners,
    lowestNet,
  }
}
